import { Color } from "./themes/color";

/** Color literals inside a `background-image` gradient, in the order the browser serializes them. */
const GRADIENT_COLOR_STOP =
  /#[0-9a-f]{3,8}|\brgba?\([^)]*\)|\bhsla?\([^)]*\)|\boklch\([^)]*\)|\bcolor\([^)]*\)/gi;

/**
 * Every distinct surface the element renders over, flattened to opaque.
 *
 * Spec-built themes paint `.themed-dashboard-card` with a translucent `linear-gradient` and no
 * `background-color`, so reading `backgroundColor` alone sees nothing and a contrast check
 * silently stops running on exactly the themes it matters most for. Gradient stops are collected
 * as separate surfaces and translucent ones composited over whatever shows through behind them.
 *
 * Shared by the metric card's icon and text contrast safeguards, which both need to know what a
 * card actually paints — including the gradient classes render JS adds when a user asks for
 * colorful cards.
 */
export const resolveRenderedSurfaces = (element: HTMLElement): Color[] => {
  const painted: string[][] = [];
  // The card lives in the viewer iframe; measure in its realm rather than the host page's.
  const view = element.ownerDocument.defaultView ?? window;
  let current: HTMLElement | null = element;

  // Outward until something opaque hides the rest.
  while (current) {
    const style = view.getComputedStyle(current);
    painted.push([style.backgroundImage, style.backgroundColor]);
    if (isOpaque(style.backgroundColor)) {
      break;
    }
    current = current.parentElement;
  }
  return flattenPaintedLayers(painted);
};

const isOpaque = (value: string): boolean => (Color.tryParse(value)?.alpha ?? 0) >= 1;

/**
 * Pure core of {@link resolveRenderedSurfaces}: `[backgroundImage, backgroundColor]` pairs ordered
 * innermost-first, reduced to the opaque surfaces text or an icon can actually sit on.
 *
 * Translucent layers are flattened against the first opaque one found behind them — stacked
 * translucency in between is rare enough not to model, and ignoring it only shifts the result
 * toward the page color.
 */
export const flattenPaintedLayers = (painted: readonly (readonly string[])[]): Color[] => {
  const layers: Color[] = [];
  let backdrop: Color | null = null;

  for (const [backgroundImage, backgroundColor] of painted) {
    const stops = parseColors(
      backgroundImage === "none" ? [] : backgroundImage.match(GRADIENT_COLOR_STOP) ?? []
    );
    // A fully opaque gradient hides everything beneath it — the element's own background-color
    // included. A gradient class render JS adds to a legacy-themed card sits over that theme's
    // solid card color, which is then never seen and must not be scored as a surface.
    if (stops.length > 0 && stops.every(stop => stop.alpha >= 1)) {
      layers.push(...stops);
      backdrop = stops[0];
      break;
    }
    // Painted beneath the element's own image, so it can back that element's translucent stops.
    const ownColor = parseColors([backgroundColor])[0];
    layers.push(...stops, ...(ownColor ? [ownColor] : []));

    if (ownColor && ownColor.alpha >= 1) {
      backdrop = ownColor;
      break;
    }
  }

  // Deliberately never a gradient stop from the same element: sibling stops sit side by side, they
  // do not stack, so a solid stop is not what shows through its translucent neighbour.
  if (!backdrop) {
    // Nothing opaque anywhere up the tree (detached node, no DOM): translucent samples alone would
    // misreport contrast, so report no surface and let the caller fail open.
    return [];
  }
  return layers.map(layer => flattenOver(layer, backdrop as Color));
};

const parseColors = (values: readonly string[]): Color[] =>
  values
    .map(value => Color.tryParse(value))
    .filter((color): color is Color => !!color && color.alpha > 0);

/** Standard src-over composite, so a translucent card reads as what the eye actually sees. */
const flattenOver = (foreground: Color, backdrop: Color): Color => {
  if (foreground.alpha >= 1) {
    return foreground;
  }
  const mix = (fg: number, bg: number) =>
    Math.round(fg * foreground.alpha + bg * (1 - foreground.alpha));
  return Color.parse(
    `rgb(${mix(foreground.r, backdrop.r)}, ${mix(foreground.g, backdrop.g)}, ${mix(
      foreground.b,
      backdrop.b
    )})`
  );
};
